import { useCallback } from "react"

import { useFieldArray, useFormContext } from "react-hook-form"

import styles from "./CompanySmsSendingRuleRowEditV2.module.scss"
import { ManuallyClaimingUnitsEdit } from "./ManuallyClaimingUnitsEdit"
import { UnitsEdit } from "./UnitsEdit"
import {
	isClaimingFunctionEnabled,
	isEncouragingRereservationEnabled,
	isManuallyClaimingEnabled,
} from "./logic"
import { useUnitsEdit } from "./useUnitsEdit"

import { FunctionVisitCountsSlider } from "@/souhl_admin/components/features/companies/sms-sending-rule/FunctionVisitCountsSlider/FunctionVisitCountsSlider"
import {
	cancelFeeTypeForNonZeroYenReservationKey,
	cancelFeeTypeForNonZeroYenReservationLabel,
	maxFeeAmount,
	maxFeePercent,
	minFeeAmount,
	minFeePercent,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/companySmsSendingRuleV2/CompanySmsSendingRuleV2.const"
import { useCancelTypeTexts } from "@/souhl_admin/lib/useCancelTypeTexts"
import {
	CompanySmsSendingRuleV2EditCancelTypeSetting,
	CompanySmsSendingRuleV2EditType,
} from "@/souhl_admin/utils/form/companySmsSendingRuleV2Edit"

import type {
	FieldErrors,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from "react-hook-form"

type Props = {
	index: number
	selectableClaimingConversionPolicyNames: string[]
	selectableEncouragingRereservationPolicyNames: string[]
	row: CompanySmsSendingRuleV2EditCancelTypeSetting
	register: UseFormRegister<CompanySmsSendingRuleV2EditType>
	setValue: UseFormSetValue<CompanySmsSendingRuleV2EditType>
	watch: UseFormWatch<CompanySmsSendingRuleV2EditType>
	errors: FieldErrors<CompanySmsSendingRuleV2EditType>
}

export const CompanySmsSendingRuleRowEditV2 = ({
	index,
	selectableClaimingConversionPolicyNames,
	selectableEncouragingRereservationPolicyNames,
	row,
	register,
	setValue,
	watch,
	errors,
}: Props) => {
	const { getCancelTypeLabel } = useCancelTypeTexts()
	const rowPath = `cancelTypeSettingInputs.${index}` as const
	const rowErrors = errors?.cancelTypeSettingInputs?.[index] ?? {}

	const minVisitCountForEncouragingRereservation =
		watch(`${rowPath}.minVisitCountForEncouragingRereservation`) ??
		row?.minVisitCountForEncouragingRereservation ??
		0

	const minVisitCountForNothing =
		watch(`${rowPath}.minVisitCountForNothing`) ??
		row?.minVisitCountForNothing ??
		0

	const minVisitCountForManuallyClaimingCancelFee =
		watch(`${rowPath}.minVisitCountForManuallyClaimingCancelFee`) ??
		row?.minVisitCountForManuallyClaimingCancelFee ??
		0

	const cancelFeeTypeForNonZeroYenReservation =
		watch(`${rowPath}.cancelFeeTypeForNonZeroYenReservation`) ??
		row?.cancelFeeTypeForNonZeroYenReservation

	const isClaimingEnabled = isClaimingFunctionEnabled(
		minVisitCountForEncouragingRereservation
	)

	const isEncouragingEnabled = isEncouragingRereservationEnabled(
		minVisitCountForEncouragingRereservation,
		minVisitCountForNothing
	)

	const isManuallyClaimingRangeEnabled = isManuallyClaimingEnabled(
		minVisitCountForManuallyClaimingCancelFee,
		minVisitCountForEncouragingRereservation
	)

	const { control } = useFormContext<CompanySmsSendingRuleV2EditType>()
	const unitsPath = `${rowPath}.encouragingRereservationUnits` as const
	const { fields: unitFields, replace: replaceUnits } = useFieldArray({
		control,
		name: unitsPath,
	})
	// NOTE: react-hook-form は setValue で内部の配列を in-place で書き換えるため、watch が返す配列の
	// 参照は変わらない。参照を依存にした useMemo だと更新を拾えないので、unit 由来の値は毎 render 計算する
	const units = watch(unitsPath) ?? []

	const settingEncouragingRereservationPolicyName = watch(
		`${rowPath}.encouragingRereservationPolicyName` as const
	)

	// 再予約案内区間 [rangeStart, rangeEnd) を unit で分割する
	const rangeStart = minVisitCountForEncouragingRereservation
	const rangeEnd = minVisitCountForNothing

	const setUnitMinVisitCount = useCallback(
		(
			unitIndex: number,
			value: number,
			options: { shouldValidate: boolean }
		) => {
			setValue(
				`${unitsPath}.${unitIndex}.minVisitCount` as const,
				value,
				options
			)
		},
		[unitsPath, setValue]
	)

	const defaultUnitPolicyName =
		settingEncouragingRereservationPolicyName ||
		selectableEncouragingRereservationPolicyNames[0] ||
		""

	// 「詳細設定」= unit が 1 件以上ある状態 (0 件なら setting の再予約案内種別を使う)
	const unitsEdit = useUnitsEdit({
		units,
		replaceUnits,
		setUnitMinVisitCount,
		rangeStart,
		rangeEnd,
		createInitialUnit: () => ({
			minVisitCount: rangeStart,
			encouragingRereservationPolicyName: defaultUnitPolicyName,
		}),
		createSplitUnit: (source, minVisitCount) => ({
			minVisitCount,
			encouragingRereservationPolicyName:
				source.encouragingRereservationPolicyName,
		}),
	})

	return (
		<div className={styles.company_sms_sending_rule_row}>
			<div className={styles.row_header}>
				{getCancelTypeLabel(row?.cancelType ?? "")}
			</div>
			<div className={styles.row_container}>
				<div className={styles.rule_header}>
					<div className={styles.rule_cell}>請求基準</div>
					<div className={styles.rule_cell}>請求金額(予約金額あり)</div>
					<div className={styles.rule_cell}>請求金額(予約金額未定)</div>
					<div className={styles.rule_cell}>請求転換種別</div>
					<div className={styles.rule_cell}>手動請求種別</div>
					<div className={styles.rule_cell}>再予約案内種別</div>
				</div>
				<div className={styles.row_body}>
					<div className={styles.rule_cell}>
						<div className={styles.slider_container}>
							<FunctionVisitCountsSlider
								values={[
									row?.minVisitCountForClaimingConversion ?? 0,
									row?.minVisitCountForManuallyClaimingCancelFee ?? 0,
									row?.minVisitCountForEncouragingRereservation ?? 0,
									row?.minVisitCountForNothing ?? 0,
								]}
								onValuesChange={(values) => {
									const [a, b, c, d] = values
									setValue(
										`${rowPath}.minVisitCountForClaimingConversion` as const,
										a,
										{ shouldValidate: true }
									)
									setValue(
										`${rowPath}.minVisitCountForManuallyClaimingCancelFee` as const,
										b,
										{ shouldValidate: true }
									)
									setValue(
										`${rowPath}.minVisitCountForEncouragingRereservation` as const,
										c,
										{ shouldValidate: true }
									)
									setValue(`${rowPath}.minVisitCountForNothing` as const, d, {
										shouldValidate: true,
									})
								}}
							/>
							{(rowErrors?.minVisitCountForClaimingConversion?.message ||
								rowErrors?.minVisitCountForEncouragingRereservation?.message ||
								rowErrors?.minVisitCountForManuallyClaimingCancelFee?.message ||
								rowErrors?.minVisitCountForNothing?.message) && (
								<div>
									{rowErrors?.minVisitCountForClaimingConversion?.message && (
										<small role="alert">
											{rowErrors?.minVisitCountForClaimingConversion?.message}
										</small>
									)}
									{rowErrors?.minVisitCountForEncouragingRereservation
										?.message && (
										<small role="alert">
											{
												rowErrors?.minVisitCountForEncouragingRereservation
													?.message
											}
										</small>
									)}
									{rowErrors?.minVisitCountForManuallyClaimingCancelFee
										?.message && (
										<small role="alert">
											{
												rowErrors?.minVisitCountForManuallyClaimingCancelFee
													?.message
											}
										</small>
									)}
									{rowErrors?.minVisitCountForNothing?.message && (
										<small role="alert">
											{rowErrors?.minVisitCountForNothing?.message}
										</small>
									)}
								</div>
							)}
						</div>
					</div>
					<div className={styles.rule_cell}>
						{isClaimingEnabled ? (
							<>
								<select
									className={styles.select}
									{...register(
										`${rowPath}.cancelFeeTypeForNonZeroYenReservation`
									)}
								>
									<option
										value={cancelFeeTypeForNonZeroYenReservationKey.PERCENT}
									>
										{
											cancelFeeTypeForNonZeroYenReservationLabel[
												cancelFeeTypeForNonZeroYenReservationKey.PERCENT
											]
										}
									</option>
									<option
										value={cancelFeeTypeForNonZeroYenReservationKey.AMOUNT}
									>
										{
											cancelFeeTypeForNonZeroYenReservationLabel[
												cancelFeeTypeForNonZeroYenReservationKey.AMOUNT
											]
										}
									</option>
								</select>
								{/* 三項演算子で書くと同じDOMと判定されて状態切り替えした時にvalueが引き継がれてしまう */}
								{cancelFeeTypeForNonZeroYenReservation ===
									cancelFeeTypeForNonZeroYenReservationKey.PERCENT && (
									<div>
										<input
											type="number"
											min={minFeePercent}
											max={maxFeePercent}
											className={`${styles.input ?? ""} ${
												rowErrors?.cancelFeePercentForNonZeroYenReservation
													? styles.error
													: ""
											}`}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault()
												}
											}}
											{...register(
												`${rowPath}.cancelFeePercentForNonZeroYenReservation`,
												{
													valueAsNumber: true,
												}
											)}
										/>
										％
										{rowErrors?.cancelFeePercentForNonZeroYenReservation
											?.message && (
											<small role="alert" className={styles.error_message}>
												{
													rowErrors?.cancelFeePercentForNonZeroYenReservation
														?.message
												}
											</small>
										)}
									</div>
								)}
								{cancelFeeTypeForNonZeroYenReservation ===
									cancelFeeTypeForNonZeroYenReservationKey.AMOUNT && (
									<div>
										<input
											type="number"
											min={minFeeAmount}
											max={maxFeeAmount}
											className={`${styles.input ?? ""} ${
												rowErrors?.cancelFeeAmountForNonZeroYenReservation
													? styles.error
													: ""
											}`}
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault()
												}
											}}
											{...register(
												`${rowPath}.cancelFeeAmountForNonZeroYenReservation`,
												{
													valueAsNumber: true,
												}
											)}
										/>
										円
										{rowErrors?.cancelFeeAmountForNonZeroYenReservation
											?.message && (
											<small role="alert" className={styles.error_message}>
												{
													rowErrors?.cancelFeeAmountForNonZeroYenReservation
														?.message
												}
											</small>
										)}
									</div>
								)}
							</>
						) : (
							<div className={styles.disabled_cell}>-</div>
						)}
					</div>
					<div className={styles.rule_cell}>
						{isClaimingEnabled ? (
							<>
								<input
									className={`${styles.input ?? ""} ${
										rowErrors?.cancelFeeAmountForZeroYenReservation
											? styles.error
											: ""
									}`}
									type="number"
									step="1"
									placeholder="0"
									{...register(
										`${rowPath}.cancelFeeAmountForZeroYenReservation`,
										{
											valueAsNumber: true,
										}
									)}
								/>
								円
								{rowErrors?.cancelFeeAmountForZeroYenReservation?.message && (
									<small role="alert" className={styles.error_message}>
										{rowErrors?.cancelFeeAmountForZeroYenReservation?.message}
									</small>
								)}
							</>
						) : (
							<div className={styles.disabled_cell}>-</div>
						)}
					</div>
					<div className={styles.rule_cell}>
						<select
							className={styles.select}
							{...register(`${rowPath}.claimingConversionPolicyName` as const)}
						>
							<option value="">-</option>
							{selectableClaimingConversionPolicyNames.map((name) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>
					</div>
					<div
						className={`${styles.rule_cell ?? ""} ${
							styles.manually_claiming_units ?? ""
						}`}
					>
						<ManuallyClaimingUnitsEdit
							index={index}
							rangeStart={minVisitCountForManuallyClaimingCancelFee}
							rangeEnd={minVisitCountForEncouragingRereservation}
							isRangeEnabled={isManuallyClaimingRangeEnabled}
							errors={errors}
						/>
					</div>
					<div
						className={`${styles.rule_cell ?? ""} ${
							styles.encourage_rereservation_policy ?? ""
						}`}
					>
						<select
							className={styles.select}
							{...register(
								`${rowPath}.encouragingRereservationPolicyName` as const
							)}
						>
							<option value="">-</option>
							{selectableEncouragingRereservationPolicyNames.map((name) => (
								<option key={name} value={name}>
									{name}
								</option>
							))}
						</select>
						{isEncouragingEnabled && (
							<UnitsEdit
								rangeStart={rangeStart}
								rangeEnd={rangeEnd}
								unitsEdit={unitsEdit}
								unitKeys={unitFields.map((field) => field.id)}
								toggleDetailDisabled={
									selectableEncouragingRereservationPolicyNames.length === 0
								}
								detailNote="再予約案内の区間を来店回数で分け、区間ごとに送付種別を選べます"
								idleNote="来店回数によって送付種別を分けたい場合は詳細設定を押してください"
								startFixedLabel="再予約案内の開始"
								renderUnitControl={(unitIndex) => (
									<select
										className={styles.select}
										{...register(
											`${unitsPath}.${unitIndex}.encouragingRereservationPolicyName` as const
										)}
									>
										{selectableEncouragingRereservationPolicyNames.map(
											(name) => (
												<option key={name} value={name}>
													{name}
												</option>
											)
										)}
									</select>
								)}
								getUnitErrorMessage={(unitIndex) =>
									rowErrors?.encouragingRereservationUnits?.[unitIndex]
										?.encouragingRereservationPolicyName?.message
								}
								rootErrorMessage={
									rowErrors?.encouragingRereservationUnits?.root?.message ||
									rowErrors?.encouragingRereservationUnits?.message
								}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
